import { ReactNode, useState } from 'react';
import { Box, Divider } from '@mui/material';
import Popover from '@mui/material/Popover';
import ReplayIcon from '@mui/icons-material/Replay';
import { anchorRef, bindTrigger } from 'material-ui-popup-state/hooks';

import { Inbound_Message_Class } from '@occc/graphql';
import { FilterButton, IconButton, Typography } from '@occc/ui-design-system';
import { useGetSiteConnectionHistory } from '@occc-app/dataAccessLayer/site/queries';

import { ConnectionHistoryFilterContainer } from './features/table-filter/connection-history-filter.container';
import { useLocationConnectionHistoryFilter } from './features/table-filter/use-location-connection-history-filter.hook';
import { Tabs } from './components/connection-history-tabs.component';
import { ConnectionHistoryTable } from './components/connection-history-table.component';

import { LocationConnectionHistoryProps } from './types';

type Tab = {
  value: Inbound_Message_Class;
  label: string;
  content: ReactNode;
};

export const LocationConnectionHistory = ({
  location,
  onReload,
}: LocationConnectionHistoryProps) => {
  const { mobileEnabled = false, loyaltyEnabled = false } =
    location.siteSystemConfiguration[0] || {};
  const [activeTabValue, setActiveTabValue] = useState<Inbound_Message_Class>(
    mobileEnabled ? Inbound_Message_Class.Mobile : Inbound_Message_Class.Loyalty,
  );

  const filterContainer = useLocationConnectionHistoryFilter();

  const {
    data,
    loading,
    paginationProps,
    refetch: refetchConnectionHistory,
  } = useGetSiteConnectionHistory(
    {
      locationUuid: location.uuid,
      inboundMessageClassEquals: activeTabValue,
      ...filterContainer.queryFilter,
    },
    10,
  );

  const onReloadHandler = () => {
    refetchConnectionHistory();
    onReload();
  };

  const tabs: Tab[] = [];

  if (mobileEnabled) {
    tabs.push({
      value: Inbound_Message_Class.Mobile,
      label: 'Mobile',
      content: (
        <ConnectionHistoryTable
          data={data}
          paginationProps={paginationProps}
          timezone={location.timezone}
          loading={loading}
        />
      ),
    });
  }

  if (loyaltyEnabled) {
    tabs.push({
      value: Inbound_Message_Class.Loyalty,
      label: 'Loyalty',
      content: (
        <ConnectionHistoryTable
          data={data}
          paginationProps={paginationProps}
          timezone={location.timezone}
          loading={loading}
        />
      ),
    });
  }

  if (tabs.length < 1) {
    return (
      <Typography color='primary' px={2} pt={1}>
        Connection history is not available.
      </Typography>
    );
  }

  return (
    <>
      <Popover
        {...filterContainer.popoverProps}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        keepMounted={false}
        sx={{
          '.MuiPopover-paper': {
            maxWidth: 460,
            minWidth: 300,
          },
        }}
      >
        <ConnectionHistoryFilterContainer
          value={filterContainer.value}
          onChange={filterContainer.onChange}
        />
      </Popover>
      <Tabs tabs={tabs} activeTabValue={activeTabValue} onChange={setActiveTabValue}>
        {(tabsComponent: ReactNode, content: ReactNode) => (
          <>
            <Box display='flex' alignItems='center' justifyContent='space-between' px={2}>
              {tabsComponent}
              <Box>
                <FilterButton
                  {...bindTrigger(filterContainer.popupState)}
                  ref={anchorRef(filterContainer.popupState)}
                  sx={{ ml: null, mr: null }}
                  badgeSx={{
                    '& .MuiBadge-dot': { top: '10px', right: '6px' },
                  }}
                  showBadge={filterContainer.isActive}
                />
                <IconButton onClick={onReloadHandler} size='medium'>
                  <ReplayIcon color='primary' sx={{ transform: 'scale(-1, 1) rotate(-45deg)' }} />
                </IconButton>
              </Box>
            </Box>
            <Divider />
            {content}
          </>
        )}
      </Tabs>
    </>
  );
};
